import * as React from "react";

type UseSearchKeyboardProps = {
  isOpen: boolean;
  results: Array<{ id: number }>;
  hasSearchQuery: boolean;
  onSelectResult: (articleId: number) => void;
  onClose: () => void;
  onClearSearch: () => void;
};

type UseSearchKeyboardReturn = {
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

const useSearchKeyboard = ({
  isOpen,
  results,
  hasSearchQuery,
  onSelectResult,
  onClose,
  onClearSearch,
}: UseSearchKeyboardProps): UseSearchKeyboardReturn => {
  const [activeIndex, setActiveIndex] = React.useState(-1);

  React.useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(-1);
  }, [isOpen, results]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (hasSearchQuery) {
          onClearSearch();
          setActiveIndex(-1);
          return;
        }
        onClose();
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((prev) => {
          if (!results.length) return -1;
          const next = prev < 0 ? 0 : prev + 1;
          return next % results.length;
        });
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((prev) => {
          if (!results.length) return -1;
          if (prev < 0) return results.length - 1;
          const next = prev - 1;
          return (next + results.length) % results.length;
        });
        return;
      }

      if (
        event.key === "Enter" &&
        results.length > 0
      ) {
        event.preventDefault();
        const safeIndex =
          activeIndex >= 0 && activeIndex < results.length ? activeIndex : 0;
        const target = results[safeIndex];
        if (target?.id) {
          onSelectResult(target.id);
        }
      }
    },
    [
      activeIndex,
      hasSearchQuery,
      isOpen,
      onClearSearch,
      onClose,
      onSelectResult,
      results,
    ]
  );

  return {
    activeIndex,
    setActiveIndex,
    handleKeyDown,
  };
};

export default useSearchKeyboard;
